import React from 'react'

import SelectTableHead, { ISelectTableHead } from './head'
import SelectTableBody from './body'
import { IData } from 'components/layout/table'
import Button from 'components/ui/button'
import Modal from 'components/ui/modal'
import Loader from 'components/ui/loader'

import isEqual from 'lodash/isEqual'

export interface ISelectTableConfig {
  dataName: string
  dataSetName: string
  head: ISelectTableHead[]
}

export interface ISelectItem extends IData {
  id: string
}

export interface IDuplicate extends ISelectItem {
  destination: number
}

interface IProps {
  config: ISelectTableConfig
  data: ISelectItem[]
  isDragDisabled?: boolean
  isLoading?: boolean
  onChange?: (items: ISelectItem[]) => void
  onDelete?: (items: ISelectItem[]) => void
  onOrderChange?: (source: number, destination: number, item: ISelectItem) => void
  onDuplicate?: (duplicates: IDuplicate[]) => void
}

interface IState {
  items: ISelectItem[]
  selected: {
    [k: string]: string
  }
  showActions: boolean
  modalOpen: boolean
}

class SelectTable extends React.Component<IProps, IState> {
  private tableBody: React.RefObject<SelectTableBody>

  constructor(props: any) {
    super(props)
    this.tableBody = React.createRef()

    this.state = {
      items: props.data,
      selected: {},
      showActions: false,
      modalOpen: false,
    }
  }

  componentDidUpdate = (oldProps: IProps) => {
    const newProps = this.props

    if (!isEqual(oldProps.data, newProps.data)) {
      this.setState({ items: newProps.data })
    }
  }

  handleOrderChange = (
    newItems: ISelectItem[],
    source: number,
    destination: number,
    item: ISelectItem,
  ) => {
    const { onOrderChange, onChange } = this.props
    if (onOrderChange) {
      onOrderChange(source, destination, item)
    }

    if (onChange) {
      onChange(newItems)
    }

    this.setState({
      items: newItems,
    })
  }

  handleSelectedChange = (selected: { [k: string]: string }) => {
    this.setState({
      selected,
    })

    const selectedCount = Object.keys(selected).length

    // Show actions if at least one item is selected
    this.setState({
      showActions: selectedCount > 0,
    })
  }

  handleToggleAll = (isSelected: boolean) => {
    if (this.tableBody.current) {
      this.tableBody.current.toggleAll(isSelected)
    }
  }

  handleModalOpen = () => {
    this.setState({
      modalOpen: true,
    })
  }

  handleModalClose = () => {
    this.setState({
      modalOpen: false,
    })
  }

  isSelected = (id: string) => this.state.selected.hasOwnProperty(id)

  duplicateSelected = () => {
    const { items } = this.state
    const { onDuplicate } = this.props

    let duplicates: IDuplicate[] = []

    items.map((item: ISelectItem, index: number) => {
      if (this.isSelected(item.id)) {
        const duplicate = {
          ...item,
        }

        duplicates.push({
          destination: index + 1,
          ...duplicate,
        })
      }

      return item
    })

    if (onDuplicate) {
      onDuplicate(duplicates)
    }
  }

  deleteSelected = () => {
    const { items } = this.state
    const { onDelete, onChange } = this.props

    let deletions: ISelectItem[] = []

    const itemsNew = items.reduce((arr: ISelectItem[], item: ISelectItem, index: number) => {
      if (this.isSelected(item.id)) {
        const deletion = {
          ...item,
        }

        deletions.push({
          ...deletion,
        })
      } else {
        arr.push(item)
      }
      return arr
    }, [])

    if (onDelete) {
      onDelete(deletions)
    }

    if (onChange) {
      onChange(itemsNew)
    }

    this.setState({
      items: itemsNew,
      selected: Object.create({}),
    })

    this.handleModalClose()
  }

  hasMultipleSelections = () => {
    const selectedCount = Object.keys(this.state.selected).length
    return selectedCount > 1
  }

  public render() {
    const { showActions, modalOpen, items } = this.state
    const { config, isDragDisabled, isLoading } = this.props

    return (
      <>
        <Modal
          headline={`${config.dataName} löschen`}
          open={modalOpen}
          onClose={this.handleModalClose}
        >
          <div className="flex justify-between mt-45">
            <Button tertiary onClick={this.handleModalClose}>
              Abbrechen
            </Button>
            <Button danger onClick={this.deleteSelected}>
              Endgültig löschen
            </Button>
          </div>
        </Modal>
        <div className="w-full mb-10">
          {showActions && (
            <div
              style={{
                paddingLeft: 28,
                paddingRight: 48,
                top: 100,
              }}
              className="sticky z-50 border-b-1 border-black bg-white"
            >
              <Button onClick={this.duplicateSelected}>{`${config.dataName} duplizieren`}</Button>
              <Button onClick={this.handleModalOpen}>{`${config.dataName} löschen`}</Button>
            </div>
          )}
          <div className="mt-65">
            <SelectTableHead head={config.head} onToggle={this.handleToggleAll} />
            {isLoading ? (
              <Loader />
            ) : (
              <SelectTableBody
                ref={this.tableBody}
                config={config}
                data={items}
                isDragDisabled={isDragDisabled}
                onOrderChange={this.handleOrderChange}
                onSelectedChange={this.handleSelectedChange}
              />
            )}
          </div>
        </div>
      </>
    )
  }
}

export default SelectTable
